import React, { Suspense, lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import RouteErrorPage from "pages/Error/RouteErrorPage";
import { ANALYTICS_ROLES, FACULTY_ROLES, RECTOR_ROLES, SUPERUSER_ROLES } from "utils/roles";

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
      color: "#717680",
      fontSize: 14,
    }}
  >
    Loading...
  </div>
);

const suspense = (node: React.ReactNode) => (
  <Suspense fallback={<PageLoader />}>{node}</Suspense>
);

const RELOADED_KEY = "chunk-reload-attempted";

// A deploy rewrites chunk file names, so a tab opened before it asks for a chunk
// that is already gone (webpack ChunkLoadError). Reload once to pick up the fresh
// index.html; if the chunk still fails, let the error bubble up to errorElement.
const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) =>
  lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.setItem(RELOADED_KEY, "false");
      return module;
    } catch (error) {
      if (sessionStorage.getItem(RELOADED_KEY) !== "true") {
        sessionStorage.setItem(RELOADED_KEY, "true");
        window.location.reload();
        return { default: (() => null) as unknown as T };
      }
      throw error;
    }
  });

const Login = lazyWithRetry(() => import("../pages/Login"));
const Forbidden403 = lazyWithRetry(() => import("pages/Forbidden"));
const Courses = lazyWithRetry(() =>
  import("pages/Courses").then((m) => ({ default: m.Courses })),
);
const NetworkErrorPage = lazyWithRetry(() => import("pages/Error/NetworkErrorPage"));
const NewDesignLive = lazyWithRetry(() => import("../pages/Live/NewDesignLive"));
const Live = lazyWithRetry(() => import("../pages/Live"));
const LiveDetail = lazyWithRetry(() => import("pages/Live/LiveDetail"));
const Users = lazyWithRetry(() => import("../pages/Users"));
const StudentList = lazyWithRetry(() => import("../pages/Users/_components/StudentList"));
const EmployeeList = lazyWithRetry(
  () => import("pages/Users/_components/EmployeeList"),
);
const UserCreate = lazyWithRetry(() => import("../pages/Users/UserCreate"));
const UserEdit = lazyWithRetry(() => import("../pages/Users/UserEdit"));
const EmployeeCreate = lazyWithRetry(() => import("../pages/Users/EmployeeCreate"));
const EmployeeEdit = lazyWithRetry(() => import("../pages/Users/EmployeeEdit"));
const AccountEdit = lazyWithRetry(() => import("../pages/AccountEdit"));
const RolePage = lazyWithRetry(() => import("../pages/Role/RolePage"));
const Support = lazyWithRetry(() => import("pages/Support"));
const Schedule = lazyWithRetry(() => import("../pages/Schedule"));
const Daily = lazyWithRetry(() => import("../pages/Schedule/Daily"));
const University = lazyWithRetry(() => import("pages/Buildings"));
const ZdPage = lazyWithRetry(() => import("pages/Buildings/ZdPage"));
const RoomPage = lazyWithRetry(() => import("pages/Buildings/RoomPage"));
const UniCamera = lazyWithRetry(() => import("../pages/Buildings/UniCamera"));
const Cameras = lazyWithRetry(() => import("pages/Cameras"));
const Roi = lazyWithRetry(() => import("pages/ROI/ROICreator"));
const ImageUploads = lazyWithRetry(() => import("pages/ImageUploads"));
const RoleUsers = lazyWithRetry(() => import("pages/RoleUsers"));
const StudentDetailForAdmin = lazyWithRetry(
  () => import("pages/AnalyticsStudents/StudentDetailForAdmin"),
);
const TeacherDetailForAdmin = lazyWithRetry(
  () => import("pages/AnalyticsTeachers/TeacherDetailForAdmin"),
);
const NoImagefaculties = lazyWithRetry(() => import("pages/NoImagefaculties"));
const NoImageStudents = lazyWithRetry(() => import("pages/NoImageStudents"));
const HemisSyncPage = lazyWithRetry(() => import("pages/HemisSync"));
const LocationList = lazyWithRetry(() => import("pages/LocationList"));
const LocationCreate = lazyWithRetry(() => import("pages/LocationCreate"));
const LocationEdit = lazyWithRetry(() => import("pages/LocationEdit"));
const LocationRoomConnect = lazyWithRetry(() => import("pages/LocationRoomConnect"));
const LocationRoomConnectEdit = lazyWithRetry(
  () => import("pages/LocationRoomConnectEdit"),
);
const LocationsListsCOnnect = lazyWithRetry(() => import("pages/LocationsListsCOnnect"));
const ExamSchedule = lazyWithRetry(() => import("pages/ExamSchedule"));
const ExamShcheduleEdit = lazyWithRetry(() => import("pages/ExamShcheduleEdit"));
const ExamScheduleCreate = lazyWithRetry(() => import("pages/ExamScheduleCreate"));
const ExamScheduleAnalytics = lazyWithRetry(() => import("pages/ExamScheduleAnalytics"));
const ExamShcheduleByID = lazyWithRetry(() => import("pages/ExamShcheduleByID"));
const PracticalAttandance = lazyWithRetry(
  () => import("pages/PracticalScheduleAttandance"),
);
const AnalyticsNewHome = lazyWithRetry(() => import("pages/AnalyticsNew"));
const AnalyticsNewFaculties = lazyWithRetry(() => import("pages/AnalyticsNew/Faculties"));
const AnalyticsNewDirections = lazyWithRetry(() => import("pages/AnalyticsNew/Directions"));
const AnalyticsNewGroups = lazyWithRetry(() => import("pages/AnalyticsNew/Groups"));
const AnalyticsNewSubjects = lazyWithRetry(() => import("pages/AnalyticsNew/Subjects"));
const AnalyticsNewPeople = lazyWithRetry(() => import("pages/AnalyticsNew/People"));
const AnalyticsNewExams = lazyWithRetry(() => import("pages/AnalyticsNew/Exams"));
const AnalyticsNewPractices = lazyWithRetry(() => import("pages/AnalyticsNew/Practices"));
const AnalyticsNewAuditoriums = lazyWithRetry(() => import("pages/AnalyticsNew/Auditoriums"));

export const router = createBrowserRouter([
  { path: "/login", element: suspense(<Login />), errorElement: <RouteErrorPage /> },
  { path: "/403", element: suspense(<Forbidden403 />), errorElement: <RouteErrorPage /> },

  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: "/networkError", element: suspense(<NetworkErrorPage />) },

      {
        element: <ProtectedRoute roles={["super_admin", "admin"]} />,
        children: [
          { path: "/networkError", element: suspense(<NetworkErrorPage />) },

          { path: "/live", element: suspense(<NewDesignLive />) },
          { path: "/new-live", element: suspense(<Live />) },
          {
            path: "/live/:auditorium_id",
            element: suspense(<LiveDetail />),
          },
          {
            path: "/live/:auditorium_id/:camera_id",
            element: suspense(<LiveDetail />),
          },

          { path: "/location/list", element: suspense(<LocationList />) },
          { path: "/location/create", element: suspense(<LocationCreate />) },
          { path: "/location/edit/:id", element: suspense(<LocationEdit />) },
          {
            path: "/location/connect",
            element: suspense(<LocationRoomConnect />),
          },
          {
            path: "/location/connect/list",
            element: suspense(<LocationsListsCOnnect />),
          },
          {
            path: "/practical-schedules",
            element: suspense(<LocationsListsCOnnect />),
          },
          {
            path: "/practical-schedules-attandance",
            element: suspense(<PracticalAttandance />),
          },
          {
            path: "/practical-schedules/edit/:id",
            element: suspense(<LocationRoomConnectEdit />),
          },

          { path: "/exams/", element: suspense(<ExamSchedule />) },
          { path: "/exams/create", element: suspense(<ExamScheduleCreate />) },
          { path: "/exams/:id", element: suspense(<ExamShcheduleByID />) },
          { path: "/exams/edit/:id", element: suspense(<ExamShcheduleEdit />) },
          {
            path: "/exams/analytics",
            element: suspense(<ExamScheduleAnalytics />),
          },

          { path: "/uploads", element: suspense(<ImageUploads />) },

          {
            path: "no-image-faculties",
            element: suspense(<NoImagefaculties />),
          },
          {
            path: "/no-image-students/:department_id",
            element: suspense(<NoImageStudents />),
          },

          {
            element: <ProtectedRoute roles={["super_admin"]} withLayout={false} />,
            children: [
              { path: "/hemis-sync", element: suspense(<HemisSyncPage />) },
            ],
          },

          { path: "/users/add", element: suspense(<UserCreate />) },
          { path: "/users/edit/:id", element: suspense(<UserEdit />) },
          {
            path: "/employees/edit/:id",
            element: suspense(<EmployeeEdit />),
          },
          {
            path: "/users/add-teacher",
            element: suspense(<EmployeeCreate />),
          },
          {
            path: "/users/student/:id/:student_id",
            element: suspense(<StudentDetailForAdmin />),
          },
          {
            path: "/users/employee/:id/:employee_id",
            element: suspense(<TeacherDetailForAdmin />),
          },

          { path: "/support", element: suspense(<Support />) },

          { path: "/university", element: suspense(<University />) },
          {
            path: "/university/buildings/:uni_id",
            element: suspense(<ZdPage />),
          },
          {
            path: "/university/buildings/:uni_id/rooms/:building_id",
            element: suspense(<RoomPage />),
          },
          {
            path: "/university/buildings/:uni_id/rooms/:building_id/camera/:camera_id",
            element: suspense(<UniCamera />),
          },

          { path: "/cameras", element: suspense(<Cameras />) },
          { path: "/roi", element: suspense(<Roi />) },
        ],
      },

      {
        element: <ProtectedRoute />,
        children: [
          { path: "/account", element: suspense(<AccountEdit />) },
          { path: "/profile", element: suspense(<AccountEdit />) },
        ],
      },

      {
        element: (
          <ProtectedRoute
            roles={[...SUPERUSER_ROLES, ...RECTOR_ROLES, ...FACULTY_ROLES]}
          />
        ),
        children: [{ path: "/courses", element: suspense(<Courses />) }],
      },

      {
        element: (
          <ProtectedRoute
            roles={SUPERUSER_ROLES}
            permissions={["view_roles", "view_user-access"]}
          />
        ),
        children: [
          { path: "/users/add-roles", element: suspense(<RolePage />) },
          { path: "/roleUsers", element: suspense(<RoleUsers />) },
        ],
      },

      {
        element: (
          <ProtectedRoute roles={ANALYTICS_ROLES} permissions={["view_statistics"]} />
        ),
        children: [
          { path: "/", element: <Navigate to="/analytics" replace /> },
          { path: "/analytics", element: suspense(<AnalyticsNewHome />) },
          {
            path: "/analytics/faculties",
            element: suspense(<AnalyticsNewFaculties />),
          },
          {
            path: "/analytics/directions",
            element: suspense(<AnalyticsNewDirections />),
          },
          {
            path: "/analytics/groups",
            element: suspense(<AnalyticsNewGroups />),
          },
          {
            path: "/analytics/subjects",
            element: suspense(<AnalyticsNewSubjects />),
          },
          {
            path: "/analytics/people",
            element: suspense(<AnalyticsNewPeople />),
          },
          {
            path: "/analytics/exams",
            element: suspense(<AnalyticsNewExams />),
          },
          {
            path: "/analytics/practices",
            element: suspense(<AnalyticsNewPractices />),
          },
          {
            path: "/analytics/auditoriums",
            element: suspense(<AnalyticsNewAuditoriums />),
          },
          {
            path: "/users/",
            element: suspense(<Users />),
            children: [
              {
                path: "/users/student",
                element: suspense(
                  <StudentList pageType="detail" date="" />,
                ),
              },
              {
                path: "/users/employee",
                element: suspense(<EmployeeList pageType="detail" />),
              },
            ],
          },
          { path: "/class-schedule", element: suspense(<Schedule />) },
          {
            path: "/class-schedule/:id/:user_id",
            element: suspense(<Daily />),
          },
        ],
      },
    ],
  },
]);
